/**
 * Zod schemas for runtime validation
 * Used to validate API responses and ensure type safety
 */
import { z } from 'zod';

/**
 * User schema
 */
export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  avatar: z.string().optional(),
  role: z.enum(['student', 'instructor', 'admin']),
  subscriptionStatus: z.enum(['none', 'trial', 'active', 'cancelled', 'expired']),
  createdAt: z.string(),
});

/**
 * Instructor schema
 */
export const InstructorSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  bio: z.string(),
  avatar: z.string(),
  coverImage: z.string().optional(),
  expertise: z.array(z.string()),
  coursesCount: z.number(),
});

/**
 * Course schema
 */
export const CourseSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  shortDescription: z.string(),
  thumbnail: z.string(),
  trailerUrl: z.string().optional(),
  paymentLink: z.string().optional(),
  instructor: InstructorSchema,
  level: z.enum(['beginner', 'intermediate', 'advanced', 'all-levels']),
  language: z.string(),
  topics: z.array(z.string()),
  duration: z.number(),
  lessonsCount: z.number(),
  studentsCount: z.number(),
  rating: z.number(),
  reviewsCount: z.number(),
  isFree: z.boolean().optional(),
  isPublished: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * Lesson resource schema
 */
export const LessonResourceSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: z.enum(['pdf', 'link', 'file']),
  url: z.string(),
});

/**
 * Lesson schema
 */
export const LessonSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  title: z.string(),
  description: z.string(),
  videoUrl: z.string(),
  duration: z.number(),
  order: z.number(),
  isFree: z.boolean(),
  resources: z.array(LessonResourceSchema),
  createdAt: z.string(),
});

/**
 * Lesson progress schema
 */
export const LessonProgressSchema = z.object({
  lessonId: z.string(),
  userId: z.string(),
  completed: z.boolean(),
  watchedSeconds: z.number(),
  lastWatchedAt: z.string(),
});

/**
 * Lesson note schema
 */
export const LessonNoteSchema = z.object({
  id: z.string(),
  lessonId: z.string(),
  userId: z.string(),
  content: z.string(),
  timestamp: z.number(),
  createdAt: z.string(),
});

/**
 * Course review schema
 */
export const CourseReviewSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  userId: z.string(),
  userName: z.string(),
  userAvatar: z.string().optional(),
  rating: z.number(),
  comment: z.string(),
  createdAt: z.string(),
});

/**
 * Auth tokens schema
 */
export const AuthTokensSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
});

/**
 * Subscription plan schema
 */
export const SubscriptionPlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number(),
  interval: z.enum(['monthly', 'annual']),
  features: z.array(z.string()),
  stripePriceId: z.string(),
});

/**
 * Checkout session schema
 */
export const CheckoutSessionSchema = z.object({
  sessionId: z.string(),
  url: z.string(),
});

/**
 * Pagination meta schema
 */
export const PaginationMetaSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  totalPages: z.number(),
});

/**
 * Generic paginated response schema
 */
export const createPaginatedSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    meta: PaginationMetaSchema,
  });

/**
 * Paginated courses schema
 */
export const PaginatedCoursesSchema = createPaginatedSchema(CourseSchema);

/**
 * Paginated lessons schema
 */
export const PaginatedLessonsSchema = createPaginatedSchema(LessonSchema);

/**
 * Paginated reviews schema
 */
export const PaginatedReviewsSchema = createPaginatedSchema(CourseReviewSchema);
